import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useQuery, useMutation } from "@apollo/client";

import { Table, Card, PageHeader, Button, FilterTypes } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { DataFormat, Language, usePermission } from "imports/Utils";

import { GetDeliveryConditionsDocument, GetDeliveryConditionsQuery } from "__generated__/view/deliveryconditions/graphql/deliveryCondition.queries.generated";
import { DeleteDeliveryConditionDocument, DeleteDeliveryConditionMutationVariables } from "__generated__/view/deliveryconditions/graphql/deliveryCondition.mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type DeliveryConditionFromQuery = ChildPropsFromArray<GetDeliveryConditionsQuery, "deliveryConditions">

const DeliveryConditions = () => {

	const navigate = useNavigate();
	const confirm = useConfirm();

	const { isAdmin, isCompanyUser, isMasterdata, isReadOnly } = usePermission();

	const { refetch, data, loading } = useQuery<GetDeliveryConditionsQuery>(GetDeliveryConditionsDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.DeliveryConditions,
		content: data?.deliveryConditions || [],
	});

	const [ deleteDeliveryCondition ] = useMutation<DeleteDeliveryConditionMutationVariables>(DeleteDeliveryConditionDocument, {
		onCompleted: () => {
			refetchAfterDelete();
			refetch();
		},
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("DELIVERY_CONDITIONS")}
					<Helmet title={intl.formatMessage({ id: "DELIVERY_CONDITIONS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{(isAdmin || isCompanyUser || isMasterdata) && !isReadOnly && (
						<Button onClick={() => navigate("/view/deliveryconditions/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					columns={[ {
						id: "key",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION_KEY" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "description",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION_DESCRIPTION" }),
						sortable: true,
						width: 600,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "showOnLocationPlanning",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION_SHOW_ON_LOCATION_PLANNING" }),
						sortable: true,
						render: (row: DeliveryConditionFromQuery) => DataFormat.boolean(row.showOnLocationPlanning),
						filterComponent: FilterTypes.BOOLEAN,
					}, {
						id: "showOnBulkPlanning",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION_SHOW_ON_BULK_PLANNING" }),
						sortable: true,
						render: (row: DeliveryConditionFromQuery) => DataFormat.boolean(row.showOnBulkPlanning),
						filterComponent: FilterTypes.BOOLEAN,
					} ]}
					rowActions={((isAdmin || isCompanyUser || isMasterdata) && !isReadOnly) ? [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/deliveryconditions/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteDeliveryCondition({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ] : undefined } />
			</Card>
		</div>
	);
};

export default DeliveryConditions;
